/**
 * Was nur die App kann (Sitzung A3, Konzept `docs/strategie/app-konzept.md` §4.5, Regeln in
 * `docs/regeln/labs-ansicht.md` „Was nur die App kann“): eine eigene kleine Hülle um die Schnittstelle von Web to
 * Native statt des npm-Pakets `webtonative` (kein neues Paket, das Lockfile bleibt unberührt). Die Aufrufe stammen
 * aus dessen Quelltext (Version 1.1.24) und der Doku von Web to Native:
 *
 * - Android ruft Methoden an `window.WebToNativeInterface` (erkannt wie im Paket an `getAndroidVersion`).
 * - iOS schickt Nachrichten an `window.webkit.messageHandlers.webToNativeInterface.postMessage({ action, … })`.
 *
 * Im Browser gibt es die Schnittstelle nicht, auch nicht mit `?app=probe`: Dann tut jede Funktion nichts und gibt
 * `false` zurück, und die Aufrufer machen weiter wie bisher (Druckfenster, neuer Tab, Download im Browser).
 */

import {
  type BewertungStand,
  type HaptikArt,
  bewertungNachMoment,
  dateiArt,
  dateinameAus,
  haptikEffekt,
  haptikErlaubt,
  mimeTypFuer,
  mitBrowserMarke,
} from "./appRegeln";

/**
 * PDFs in der App anzeigen (`openPDF`) braucht den Zusatz „PDF Viewer“ im Dashboard von Web to Native. Bis Daniel ihn
 * eingeschaltet hat (Frage 402), lädt die App ein PDF herunter und öffnet es (Android in der PDF-App, iOS in der
 * Vorschau mit Teilen und Drucken).
 */
export const PDF_ANZEIGE_IN_DER_APP = false;

/**
 * Um eine Bewertung im Store bitten, höchstens einmal nach einem guten Moment (Frage 401, Daniel 18.10.2026: „Ja, nach
 * dem dritten Angebot“). Auf `false` fragt die App nie.
 */
export const APP_BEWERTUNG = true;

type AndroidSchnittstelle = Record<string, unknown>;
interface IosSchnittstelle {
  postMessage: (nachricht: Record<string, unknown>) => void;
}
interface AppFenster {
  WebToNativeInterface?: AndroidSchnittstelle;
  webkit?: { messageHandlers?: { webToNativeInterface?: IosSchnittstelle } };
}

export type AppGeraet = "android" | "ios";

function fenster(): AppFenster | null {
  return typeof window === "undefined" ? null : (window as unknown as AppFenster);
}

/** Läuft die Seite in unserer App, und unter welchem System? Nur an der Schnittstelle, nie am User-Agent. */
export function appGeraet(): AppGeraet | null {
  const w = fenster();
  if (!w) return null;
  if (typeof w.WebToNativeInterface?.getAndroidVersion === "function") return "android";
  if (typeof w.webkit?.messageHandlers?.webToNativeInterface?.postMessage === "function") return "ios";
  return null;
}

/** Eine Methode der Android-Schnittstelle aufrufen, wenn es sie gibt. */
function android(name: string, ...werte: unknown[]): boolean {
  const schnittstelle = fenster()?.WebToNativeInterface;
  const methode = schnittstelle?.[name];
  if (typeof methode !== "function") return false;
  try {
    (methode as (...w: unknown[]) => unknown).apply(schnittstelle, werte);
    return true;
  } catch (e) {
    console.warn(`[App] ${name} ging nicht:`, e);
    return false;
  }
}

/** Eine Nachricht an die iOS-Schnittstelle schicken, wenn es sie gibt. */
function ios(nachricht: Record<string, unknown>): boolean {
  const kanal = fenster()?.webkit?.messageHandlers?.webToNativeInterface;
  if (typeof kanal?.postMessage !== "function") return false;
  try {
    kanal.postMessage(nachricht);
    return true;
  } catch (e) {
    console.warn(`[App] ${String(nachricht.action)} ging nicht:`, e);
    return false;
  }
}

// ─── Start und Rahmen ───────────────────────────────────────────────────────

let startbildWeg = false;

/**
 * Startbild ausblenden, sobald die erste Seite steht. Greift nur, wenn das Startbild in Web to Native auf „JS Trigger“
 * steht (`splashScreenJsTrigger`); sonst bleibt es bei der eingestellten Zeit. Unter Android dazu der ältere Aufruf
 * `hideSplashScreen` aus dem Paket. Nur einmal je Start.
 */
export function startbildAusblenden(): boolean {
  if (startbildWeg) return false;
  const geraet = appGeraet();
  if (!geraet) return false;
  startbildWeg = true;
  if (geraet === "android") {
    const neu = android("splashScreenJsTrigger");
    const alt = android("hideSplashScreen");
    return neu || alt;
  }
  return ios({ action: "splashScreenJsTrigger" });
}

let letzteLeiste: string | null = null;

/** Statusleiste in der Farbe der Seite (`#RRGGBB`); `dark` heißt dunkle Symbole. Schickt nur, was sich geändert hat. */
export function statusleiste(farbe: string, stil: "dark" | "light"): boolean {
  const geraet = appGeraet();
  if (!geraet) return false;
  const schluessel = `${farbe}|${stil}`;
  if (schluessel === letzteLeiste) return true;
  const ok =
    geraet === "android"
      ? android("statusBar", JSON.stringify({ color: farbe, style: stil }))
      : ios({ action: "statusBar", color: farbe, style: stil });
  if (ok) letzteLeiste = schluessel;
  return ok;
}

/** Ziehen zum Aktualisieren an oder aus (aus, solange die neue Ansicht offen ist: stört das Wischen im Kursplayer). */
export function ziehenZumAktualisieren(an: boolean): boolean {
  const geraet = appGeraet();
  if (geraet === "android") return android("enableSwipeRefresh", an);
  if (geraet === "ios") return ios({ action: "setPullToRefresh", enable: an });
  return false;
}

/**
 * Eigene Zurück-Taste unter Android: Ist sie an, geht die App nicht mehr selbst zurück, sondern ruft
 * `window.customBackHandling()` (Doku „Disable Back Button“). Unter iOS würde derselbe Aufruf das Wischen vom Rand
 * abschalten, deshalb nur Android.
 */
export function eigeneZurueckTaste(an: boolean): boolean {
  if (appGeraet() !== "android") return false;
  return android("customBackHandling", JSON.stringify({ enable: an }));
}

/** Die App verlassen (Zurück-Taste auf der Übersicht). */
export function appVerlassen(): boolean {
  const geraet = appGeraet();
  if (geraet === "android") return android("closeApp");
  if (geraet === "ios") return ios({ action: "closeApp" });
  return false;
}

// ─── Haptik ─────────────────────────────────────────────────────────────────

let letzteHaptik: number | null = null;

/** Kurze Haptik (Herz gesetzt, Lektion erledigt, gespeichert); höchstens eine je 0,8 Sekunden. */
export function haptik(art: HaptikArt): boolean {
  const geraet = appGeraet();
  if (!geraet) return false;
  const jetzt = Date.now();
  if (!haptikErlaubt(jetzt, letzteHaptik)) return false;
  letzteHaptik = jetzt;
  const effect = haptikEffekt(art);
  return geraet === "android" ? android("haptikEffect", JSON.stringify({ effect })) : ios({ action: "haptikEffect", effect });
}

// ─── Teilen und nach außen ──────────────────────────────────────────────────

/** Einen Link über das Teilen des Systems weitergeben (Lektion). */
export function linkTeilen(url: string): boolean {
  const geraet = appGeraet();
  if (geraet === "android") return android("openShareIntent", url);
  if (geraet === "ios") return ios({ action: "share", url });
  return false;
}

/** Eine fremde Seite (Zoom, Google, Canva …) im Browser des Geräts öffnen statt in der App ohne Zurück. */
export function imBrowserOeffnen(url: string): boolean {
  const geraet = appGeraet();
  if (geraet === "android") return android("openUrlInBrowser", url);
  if (geraet === "ios") {
    window.location.href = mitBrowserMarke(url);
    return true;
  }
  return false;
}

// ─── Dateien ────────────────────────────────────────────────────────────────

/** Blob-Adressen lebt die App nur kurz; zwei Minuten reichen ihr zum Lesen. */
const SPEICHER_FRIST_MS = 120_000;

/**
 * Eine Datei, die schon im Speicher liegt (erzeugtes PDF, Lied in neuer Tonart), an die App geben. `teilen`: unter
 * iOS gleich das Teilen des Systems (darin Drucken, Sichern, Mail); sonst öffnet die App die Datei (Android in der
 * passenden App, von dort aus teilen und drucken).
 */
export function dateiWeitergeben(blob: Blob, dateiname: string, o: { teilen: boolean }): boolean {
  const geraet = appGeraet();
  if (!geraet) return false;
  const url = URL.createObjectURL(blob);
  window.setTimeout(() => URL.revokeObjectURL(url), SPEICHER_FRIST_MS);
  if (geraet === "ios") {
    return ios({
      action: "downloadBlobFile",
      fileName: dateiname,
      url,
      shareFileAfterDownload: o.teilen,
      openFileAfterDownload: !o.teilen,
    });
  }
  return android(
    "downloadFile",
    JSON.stringify({ url, fileName: dateiname, isBlob: true, mimeType: blob.type || mimeTypFuer(dateiname), openFileAfterDownload: true }),
  );
}

/**
 * Eine Datei aus dem Netz herunterladen und öffnen. Unter iOS erst in den Speicher geholt (so öffnet die App sie
 * gleich), geht das nicht (fremde Seite ohne Freigabe), lädt die App sie selbst.
 */
export function dateiHerunterladen(url: string, dateiname?: string): boolean {
  const geraet = appGeraet();
  if (!geraet) return false;
  const name = dateiname?.trim() || dateinameAus(url, null);
  if (geraet === "android") {
    return android("downloadFile", JSON.stringify({ url, fileName: name, mimeType: mimeTypFuer(name), openFileAfterDownload: true }));
  }
  void fetch(url)
    .then((antwort) => {
      if (!antwort.ok) throw new Error(`HTTP ${antwort.status}`);
      return antwort.blob();
    })
    .then((blob) => dateiWeitergeben(blob, name, { teilen: false }))
    .catch(() => ios({ action: "downloadFile", downloadUrl: url }));
  return true;
}

/**
 * Ein PDF in der App zeigen: mit dem Zusatz „PDF Viewer“ im eigenen Fenster, sonst herunterladen und öffnen. Der
 * Dateiname kommt aus der Adresse; hat die keinen (Belege von Stripe enden auf `/pdf`), aus dem Titel.
 */
export function pdfAnzeigen(url: string, titel?: string): boolean {
  const geraet = appGeraet();
  if (!geraet) return false;
  if (PDF_ANZEIGE_IN_DER_APP) {
    const nachricht = { url, title: titel ?? "" };
    return geraet === "android" ? android("openPDF", JSON.stringify(nachricht)) : ios({ action: "openPDF", ...nachricht });
  }
  const ausAdresse = dateinameAus(url, null);
  const ausTitel = titel?.replace(/[\\/:*?"<>|]+/g, "").trim().slice(0, 100);
  return dateiHerunterladen(url, ausAdresse === "Dokument.pdf" && ausTitel ? `${ausTitel}.pdf` : ausAdresse);
}

/**
 * Statt `window.open(url, "_blank")` in der neuen Ansicht: In der App kommt ein PDF in die Anzeige, eine Datei in den
 * Download und eine fremde Seite in den Browser des Geräts. Gibt `false` zurück, wenn es keine App gibt; dann öffnet
 * der Aufrufer wie bisher.
 */
export function appOeffnen(url: string, o: { titel?: string; dateiname?: string } = {}): boolean {
  if (!appGeraet()) return false;
  const art = dateiArt(url);
  if (art === "pdf") return pdfAnzeigen(url, o.titel);
  if (art === "datei") return dateiHerunterladen(url, o.dateiname);
  return imBrowserOeffnen(url);
}

// ─── Drucken ────────────────────────────────────────────────────────────────

/**
 * Ein fertiges Druckblatt (HTML) drucken. Nur Android kann das (`print` mit `type: "html"`, Doku „Printing
 * Options“); iOS druckt in einer App kein Fenster, dort wird das Blatt ein PDF (`blattAlsPdf()`).
 */
export function htmlDrucken(html: string): boolean {
  if (appGeraet() !== "android") return false;
  return android("print", JSON.stringify({ type: "html", url: html }));
}

// ─── Bewertung ──────────────────────────────────────────────────────────────

const BEWERTUNG_SCHLUESSEL = "mk-app-bewertung";

/** Um eine Bewertung bitten (nur mit `APP_BEWERTUNG`, Frage 401). Ob das Fenster kommt, entscheidet der Store. */
export function bewertungAnfragen(): boolean {
  if (!APP_BEWERTUNG) return false;
  const geraet = appGeraet();
  if (geraet === "android") return android("showInAppReview");
  if (geraet === "ios") return ios({ action: "showAppRating" });
  return false;
}

/**
 * Ein guter Moment (Angebot gespeichert): beim dritten bittet die App einmal um eine Bewertung, danach nie wieder
 * (`bewertungNachMoment()`). Solange `APP_BEWERTUNG` aus ist, zählt sie nicht einmal mit.
 */
export function guterMoment(): boolean {
  if (!APP_BEWERTUNG || !appGeraet()) return false;
  try {
    const roh = window.localStorage.getItem(BEWERTUNG_SCHLUESSEL);
    const { stand, fragen } = bewertungNachMoment(roh ? (JSON.parse(roh) as Partial<BewertungStand>) : null);
    window.localStorage.setItem(BEWERTUNG_SCHLUESSEL, JSON.stringify(stand));
    return fragen ? bewertungAnfragen() : false;
  } catch {
    return false;
  }
}
